import type { ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Logo } from '@rydres/ui';
import { useRiderBar } from './lib/passenger.js';
import { BookingPage } from './screens/BookingPage.js';
import { Landing } from './screens/Landing.js';
import { Privacy, Terms } from './screens/Legal.js';
import { AccountPage, JoinPage } from './screens/Passenger.js';
import { PeoplePage } from './screens/People.js';
import { HomePage } from './screens/RiderHome.js';
import { ServicesPage } from './screens/RiderServices.js';
import { RiderTabs } from './screens/RiderTabs.js';
import { QuotePage } from './screens/QuotePage.js';
import { DriverProfilePage } from './screens/DriverProfile.js';
import { RequestPage } from './screens/RideRequest.js';
import { RiderBookingPage } from './screens/RiderBooking.js';
import { PaymentsPage } from './screens/RiderPayments.js';
import { UpdatesPage } from './screens/RiderUpdates.js';
import { StatusPage } from './screens/StatusPage.js';
import { WaybillRedirect } from './screens/WaybillRedirect.js';

/**
 * A thin bar on every page: for a signed-in passenger the brand and, on a wide screen, their tabs;
 * otherwise a link to the account. The legal pages carry their own.
 */
function SiteHeader({ rider }: { rider: { signedIn: boolean; unread: number } }) {
  const { pathname } = useLocation();
  if (pathname === '/privacy' || pathname === '/terms') return null;
  return (
    <header className="mx-auto flex max-w-xl items-center justify-between gap-4 px-4 pt-3 text-xs">
      {rider.signedIn || pathname !== '/' ? (
        <Link to={rider.signedIn ? '/me' : '/'} className="flex min-h-11 items-center">
          <Logo size={24} className="text-[15px]" />
        </Link>
      ) : (
        <span />
      )}
      {rider.signedIn ? (
        <div className="flex items-center gap-2">
          <RiderTabs unread={rider.unread} placement="top" />
          <Link to="/me/account" aria-label="Settings" className="flex h-11 w-11 items-center justify-center rounded-full text-muted hover:text-ink">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 15a3 3 0 100-6 3 3 0 000 6zm7-3l2 1-1 3-2-.5a7 7 0 01-1.5 1.5l.5 2-3 1-1-2a7 7 0 01-2 0l-1 2-3-1 .5-2A7 7 0 015 15.5L3 16l-1-3 2-1a7 7 0 010-2l-2-1 1-3 2 .5A7 7 0 016.5 5L6 3l3-1 1 2a7 7 0 012 0l1-2 3 1-.5 2A7 7 0 0117 6.5l2-.5 1 3-2 1a7 7 0 010 2z" />
            </svg>
          </Link>
        </div>
      ) : (
        pathname !== '/me' && <Link to="/me" className="font-bold text-accent-text">Your account</Link>
      )}
    </header>
  );
}

/** Leaves room on the right of a wide screen while a conversation is open there (see `ChatSheet`), header included. */
function Frame({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const rider = useRiderBar(pathname);
  const tabs = rider.signedIn && pathname !== '/privacy' && pathname !== '/terms';
  const chat = pathname === '/me/people' && params.has('chat');
  return (
    <div className={[chat && 'lg:pr-[440px]', tabs && 'pb-20 md:pb-0'].filter(Boolean).join(' ') || undefined}>
      <SiteHeader rider={rider} />
      {children}
      {tabs && <RiderTabs unread={rider.unread} placement="bottom" />}
    </div>
  );
}

function OldLink({ to }: { to: 'with' | 'chat' }) {
  const { handle = '', id = '' } = useParams();
  return <Navigate to={`/me/people?${to}=${encodeURIComponent(to === 'with' ? handle : id)}`} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Frame>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/b/:token" element={<StatusPage />} />
          <Route path="/q/:token" element={<QuotePage />} />
          <Route path="/w/:token" element={<WaybillRedirect />} />
          <Route path="/me" element={<HomePage />} />
          <Route path="/me/booking" element={<RiderBookingPage />} />
          <Route path="/me/drivers/:handle" element={<DriverProfilePage />} />
          <Route path="/me/payments" element={<PaymentsPage />} />
          <Route path="/me/request" element={<RequestPage />} />
          <Route path="/me/request/:handle" element={<RequestPage />} />
          <Route path="/me/updates" element={<UpdatesPage />} />
          <Route path="/me/services" element={<ServicesPage />} />
          <Route path="/me/people" element={<PeoplePage />} />
          <Route path="/me/account" element={<AccountPage />} />
          {/* Links from before messages moved into People: texts already sent, older status pages. */}
          <Route path="/me/messages" element={<Navigate to="/me/people" replace />} />
          <Route path="/me/messages/with/:handle" element={<OldLink to="with" />} />
          <Route path="/me/messages/:id" element={<OldLink to="chat" />} />
          <Route path="/:handle/join" element={<JoinPage />} />
          <Route path="/:handle" element={<BookingPage />} />
        </Routes>
      </Frame>
    </BrowserRouter>
  );
}
